/**
 * Utility functions for recording custom metrics to both AWS RUM and Datadog RUM
 */

import { awsRumClient } from './awsrum';
import { isDatadogInitialized } from './datadogrum';

/**
 * Records a custom timing metric to AWS CloudWatch RUM
 * @param metricName - Name of the metric to record
 * @param durationMs - Duration in milliseconds
 */
export const recordAWSRumTiming = (metricName: string, durationMs: number) => {
  try {
    if (awsRumClient) {
      awsRumClient.recordEvent(metricName, {
        duration: durationMs,
        timestamp: Date.now(),
      });
    }
  } catch (error) {
    console.error('Error recording AWS RUM timing:', error);
  }
};

/**
 * Records a custom timing metric to Datadog RUM
 * @param metricName - Name of the metric to record
 * @param durationMs - Duration in milliseconds
 */
export const recordDatadogRumTiming = async (metricName: string, durationMs: number) => {
  try {
    if (isDatadogInitialized) {
      const { datadogRum } = await import('@datadog/browser-rum-slim');

      datadogRum.addTiming(metricName, durationMs);

      datadogRum.addAction(metricName, {
        duration: durationMs,
        timestamp: Date.now(),
      });
    }
  } catch (error) {
    console.error('Error recording Datadog RUM timing:', error);
  }
};

/**
 * Records a component render timing to both AWS RUM and Datadog RUM
 * Also creates a performance mark/measure for Chrome DevTools
 * @param componentName - Name of the component being tracked
 * @param startTime - Performance timing start (defaults to navigation start)
 */
export const recordComponentRenderTime = (componentName: string, startTime: number = 0) => {
  const renderTime = performance.now();
  const timeElapsed = startTime > 0 ? renderTime - startTime : renderTime;

  const metricName = `component.render.${componentName}`;

  recordAWSRumTiming(metricName, timeElapsed);
  recordDatadogRumTiming(metricName, timeElapsed);

  try {
    performance.mark(metricName);

    performance.measure(
      `${metricName}-measure`,
      undefined, // Uses navigation start as default
      metricName,
    );
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('User Timing API not available:', error);
    }
  }

  if (process.env.NODE_ENV !== 'production') {
    console.log(`[RUM Metric] ${metricName}: ${timeElapsed.toFixed(2)}ms`);
  }

  return timeElapsed;
};
